import type { ReportExportFileReferenceDto } from "@fp/admin-api-client";
import { toast as sonnerToast } from "sonner";
import type { ReportExportClient } from "@/lib/report-export-clients";
import type { SignalRNotification } from "@/lib/signalr";
import { toast, toastActionClassName } from "@/lib/toast";
import { router } from "@/router";

type ShowToast = typeof sonnerToast.info;

async function fetchFile(client: ReportExportClient, exportId: string): Promise<ReportExportFileReferenceDto | null> {
	try {
		return (await client.getById(exportId)).data.fileReference ?? null;
	} catch {
		return null;
	}
}

/**
 * The ready notification hands over the file itself, whatever report was exported. The export is
 * read as the toast is built so the action is a real link the browser treats as a user download.
 * When the file is not there or the link has expired by the time it is clicked, the action lands
 * on the download page instead, which reads the export again. The toast carries the export's id so
 * the page that requested the export can dismiss it when its own download link is clicked.
 */
export async function showReportExportReadyToast(showToast: ShowToast, notification: SignalRNotification, exportId: string, client: ReportExportClient) {
	const downloadPage = `${client.downloadPage}/${exportId}`;
	const file = await fetchFile(client, exportId);

	if (!file) {
		showToast(notification.title, {
			id: exportId,
			description: notification.message,
			duration: Infinity,
			action: { label: "Download", onClick: () => router.history.push(downloadPage) },
		});

		return;
	}

	const toastId = showToast(notification.title, {
		id: exportId,
		description: notification.message,
		duration: Infinity,
		action: (
			<a
				data-button
				href={file.url}
				target="_blank"
				rel="noopener"
				className={toastActionClassName}
				onClick={(event) => {
					sonnerToast.dismiss(toastId);

					if (Date.now() >= Date.parse(file.expiresAt)) {
						event.preventDefault();
						router.history.push(downloadPage);

						return;
					}

					client.markDownloaded(exportId).catch(() => toast.error("The download was opened but could not be recorded."));
				}}
			>
				Download
			</a>
		),
	});
}
